import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { Typography, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { FormControl} from '@mui/base/FormControl';
import { Input} from '@mui/base/Input';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import SharePointService from '../../services/sharepoint.service';

import './forms.css';

const KontaktdatenHV = () => {
    const [nameField, nameMeta] = useField('name');
    const [emailField, emailMeta] = useField('email');
    const [telField, telMeta] = useField('tel');
    const [whgField, whgMeta] = useField('whg');
    const [addressField, addressMeta] = useField('address');
    const [projectField, projectMeta] = useField('project');

    const { setFieldValue } = useFormikContext();

    // Kunde
    const [kunden, setKunden] = useState([]);
    const [selectedKunde, setSelectedKunde] = useState(null);
    const [loadingKunden, setLoadingKunden] = useState(true);

    // Bauvorhaben
    const [projekte, setProjekte] = useState([]);
    const [selectedProjekt, setSelectedProjekt] = useState(null);
    const [loadingProjekte, setLoadingProjekte] = useState(true);

    // Wohnung (abhängig vom gewählten Bauvorhaben)
    const [wohnungen, setWohnungen] = useState([]);
    const [selectedWohnung, setSelectedWohnung] = useState(null);
    const [loadingWohnungen, setLoadingWohnungen] = useState(false);

    useEffect(() => {
        const loadKunden = async () => {
            try {
                const data = await SharePointService.getCustomersForHausverwaltung();
                setKunden(data || []);
            } catch (error) {
                console.error('Fehler beim Laden des Kundenverzeichnisses:', error);
            } finally {
                setLoadingKunden(false);
            }
        };
        const loadProjekte = async () => {
            try {
                const data = await SharePointService.getProjekteForHausverwaltung();
                setProjekte(data || []);
            } catch (error) {
                console.error('Fehler beim Laden der Bauvorhaben:', error);
            } finally {
                setLoadingProjekte(false);
            }
        };
        loadKunden();
        loadProjekte();
    }, []);

    const handleKundeChange = (event, kunde) => {
        setSelectedKunde(kunde);

        setFieldValue('name', kunde?.name || '');
        setFieldValue('address', kunde ? [kunde.adresse, kunde.plz, kunde.ort].filter(Boolean).join(', ') : '');
        setFieldValue('email', kunde?.email || '');
        setFieldValue('tel', kunde?.telefon || '');
    };

    const handleProjektChange = async (event, projekt) => {
        setSelectedProjekt(projekt);
        setFieldValue('project', projekt ? String(projekt.id) : '');

        // Wohnung zurücksetzen, da abhängig vom Bauvorhaben
        setSelectedWohnung(null);
        setFieldValue('whg', '');
        setWohnungen([]);

        if (!projekt) return;

        setLoadingWohnungen(true);
        try {
            const data = await SharePointService.getWohnungenByProjekt(projekt.id);
            setWohnungen(data || []);
        } catch (error) {
            console.error('Fehler beim Laden der Wohnungen:', error);
        } finally {
            setLoadingWohnungen(false);
        }
    };

    const handleWohnungChange = (event, wohnung) => {
        setSelectedWohnung(wohnung);
        setFieldValue('whg', wohnung ? String(wohnung.id) : '');
    };

    return (
        <div>
            <h2>Kontaktinformationen</h2>
            <Grid container spacing={2} my={2}>
                <Grid item xs={12} md={8}>
                    <Hidden mdUp>
                        <Grid item xs={12} md={4} pb={5}>
                            <Box sx={{ backgroundColor: '#F0F2F5', p: 4 }}>
                                <Typography variant="h5" sx={{color: '#3a8787', pb: 1}}>Wichtig!</Typography>
                                <Typography variant="p">Bitte wählen Sie zuerst den Kunden sowie das Bauvorhaben aus. Die Wohnung wird danach passend zum Bauvorhaben geladen.</Typography>
                            </Box>
                        </Grid>
                    </Hidden>
                    <Grid container spacing={2}>
                        {/* Kunde - durchsuchbar */}
                        <Grid item xs={12} md={10}>
                            <FormControl className='wb-input-wrapper' required>
                                <label>Kunde</label>
                                <Autocomplete
                                    options={kunden}
                                    getOptionLabel={(option) => option.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={selectedKunde}
                                    onChange={handleKundeChange}
                                    loading={loadingKunden}
                                    noOptionsText="Kein Kunde gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Bitte wählen oder suchen"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingKunden ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={10}>
                            <FormControl className='wb-input-wrapper' {...nameField} required type="text" error={nameMeta.touched && nameMeta.error ? true : false} >
                                <label>Name</label>
                                <Input name="name" />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={10}>
                            <FormControl className='wb-input-wrapper' {...addressField} required error={addressMeta.touched && addressMeta.error ? true : false} >
                                <label>Adresse</label>
                                <Input name="address" type="text"/>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormControl className='wb-input-wrapper' {...emailField} required  error={emailMeta.touched && emailMeta.error ? true : false} >
                                <label>E-Mail</label>
                                <Input name="email" type="text"/>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormControl className='wb-input-wrapper' {...telField} required error={telMeta.touched && telMeta.error ? true : false} >
                                <label>Telefon</label>
                                <Input name="tel" type="tel"/>
                                {telMeta.error && (
                                    <Typography sx={{ color: 'red', fontSize: '0.875rem', mt: 0.5 }}>
                                        {telMeta.error}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Bauvorhaben - durchsuchbar, wird zuerst gewählt */}
                        <Grid item xs={12} md={5}>
                            <FormControl
                                className='wb-input-wrapper'
                                required
                                error={projectMeta.touched && projectMeta.error ? true : false}
                            >
                                <label>Bauvorhaben</label>
                                <Autocomplete
                                    options={projekte}
                                    getOptionLabel={(option) => option.title || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={selectedProjekt}
                                    onChange={handleProjektChange}
                                    loading={loadingProjekte}
                                    noOptionsText="Kein Bauvorhaben gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Bitte wählen oder suchen"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingProjekte ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                {projectMeta.touched && projectMeta.error && (
                                    <Typography sx={{ color: 'red', fontSize: '0.875rem', mt: 0.5 }}>
                                        {projectMeta.error}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Wohnung - durchsuchbar, abhängig vom gewählten Bauvorhaben */}
                        <Grid item xs={12} md={5}>
                            <FormControl
                                className='wb-input-wrapper'
                                required
                                error={whgMeta.touched && whgMeta.error ? true : false}
                            >
                                <label>Wohnungsnummer</label>
                                <Autocomplete
                                    options={wohnungen}
                                    getOptionLabel={(option) => option.title || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={selectedWohnung}
                                    onChange={handleWohnungChange}
                                    loading={loadingWohnungen}
                                    disabled={!selectedProjekt}
                                    noOptionsText={selectedProjekt ? "Keine Wohnung gefunden" : "Bitte zuerst Bauvorhaben wählen"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={selectedProjekt ? "Bitte wählen oder suchen" : "Erst Bauvorhaben wählen"}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingWohnungen ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                {whgMeta.touched && whgMeta.error && (
                                    <Typography sx={{ color: 'red', fontSize: '0.875rem', mt: 0.5 }}>
                                        {whgMeta.error}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
                <Hidden mdDown>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ backgroundColor: '#F0F2F5', p: 4 }}>
                            <Typography variant="h5" sx={{color: '#3a8787', pb: 1}}>Wichtig!</Typography>
                            <Typography variant="p">Bitte wählen Sie zuerst den Kunden sowie das Bauvorhaben aus. Die Wohnung wird danach passend zum Bauvorhaben geladen.</Typography>
                        </Box>
                    </Grid>
                </Hidden>
            </Grid>
        </div>
    );
};

export default KontaktdatenHV;